import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { RoomSettings } from "@hitster/shared";
import { GAME_CONFIG } from "@hitster/shared";
import { SoloStartModal } from "../components/SoloStartModal";
import { useGame } from "../hooks/useGameContext";
import { getCreateRoomSettingsDraft, saveCreateRoomSettingsDraft } from "../lib/formDrafts";
import { clearSession } from "../lib/storage";

export function HomePage() {
  const {
    nickname,
    setNickname,
    connected,
    room,
    game,
    playerId,
    emitUserAction,
    actionError,
    clearActionError,
  } = useGame();
  const navigate = useNavigate();
  const [startingSolo, setStartingSolo] = useState(false);
  const [soloModalOpen, setSoloModalOpen] = useState(false);
  const pendingSoloRef = useRef(false);
  const [soloSettings, setSoloSettingsState] = useState<RoomSettings>(() => getCreateRoomSettingsDraft());

  const setSoloSettings = (update: RoomSettings | ((prev: RoomSettings) => RoomSettings)) => {
    setSoloSettingsState((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      saveCreateRoomSettingsDraft(next);
      return next;
    });
  };

  const goCreate = () => {
    clearSession();
    navigate("/create");
  };

  const goJoin = () => {
    navigate("/join");
  };

  const openSoloModal = () => {
    if (!connected || nickname.length < 2) return;
    clearActionError();
    setSoloModalOpen(true);
  };

  const closeSoloModal = () => {
    if (startingSolo) return;
    setSoloModalOpen(false);
  };

  const confirmSolo = () => {
    if (!connected || nickname.length < 2) return;
    clearActionError();
    setStartingSolo(true);
    if (room && playerId) {
      pendingSoloRef.current = false;
      const sent = emitUserAction("game:start");
      if (!sent) setStartingSolo(false);
      return;
    }
    clearSession();
    pendingSoloRef.current = true;
    const sent = emitUserAction("room:create", {
      nickname,
      settings: soloSettings,
    });
    if (!sent) {
      pendingSoloRef.current = false;
      setStartingSolo(false);
    }
  };

  useEffect(() => {
    if (!room || !playerId || !pendingSoloRef.current) return;
    pendingSoloRef.current = false;
    emitUserAction("game:start");
  }, [room, playerId, emitUserAction]);

  useEffect(() => {
    if (game && game.phase !== "LOBBY" && startingSolo) {
      setStartingSolo(false);
      setSoloModalOpen(false);
      navigate("/game");
    }
  }, [game, startingSolo, navigate]);

  useEffect(() => {
    if (actionError && startingSolo) {
      setStartingSolo(false);
    }
  }, [actionError, startingSolo]);

  return (
    <div className="max-w-4xl mx-auto text-center space-y-10 pt-8">
      <div className="relative">
        <div className="absolute inset-0 flex justify-center opacity-30 pointer-events-none">
          <div className="w-48 h-48 rounded-full border-8 border-violet-500/30 vinyl-spin" />
        </div>
        <h1 className="cw-title text-5xl md:text-7xl font-extrabold relative">
          {GAME_CONFIG.name}
        </h1>
        <p className="text-xl text-violet-200/70 mt-4">{GAME_CONFIG.tagline}</p>
      </div>

      <div className="cw-panel p-8 max-w-md mx-auto space-y-4 text-left">
        <label className="block">
          <span className="text-sm text-violet-300">Votre pseudonyme</span>
          <input
            className="cw-input mt-1"
            value={nickname}
            onChange={(e) => setNickname(e.target.value.trim())}
            placeholder="Ex : DJNova"
            minLength={2}
            maxLength={24}
            aria-label="Pseudonyme"
          />
        </label>
        <p className={`text-xs ${connected ? "text-green-400" : "text-yellow-400"}`}>
          {connected ? "Connecté au serveur" : "Connexion en cours…"}
        </p>
      </div>

      {actionError && (
        <p className="text-sm text-red-400" role="alert">{actionError}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          type="button"
          className="cw-btn cw-btn-primary text-lg px-10"
          disabled={nickname.length < 2 || !connected || startingSolo}
          onClick={openSoloModal}
        >
          Jouer seul
        </button>
        <button
          type="button"
          className="cw-btn cw-btn-secondary text-lg px-10"
          disabled={nickname.length < 2 || !connected || startingSolo}
          onClick={goCreate}
        >
          Créer une partie
        </button>
        <button
          type="button"
          className="cw-btn cw-btn-secondary text-lg px-10"
          disabled={nickname.length < 2 || !connected || startingSolo}
          onClick={goJoin}
        >
          Rejoindre une partie
        </button>
      </div>

      <SoloStartModal
        open={soloModalOpen}
        starting={startingSolo}
        settings={soloSettings}
        actionError={actionError}
        onSettingsChange={(patch) => setSoloSettings((prev) => ({ ...prev, ...patch }))}
        onConfirm={confirmSolo}
        onClose={closeSoloModal}
      />
    </div>
  );
}
